<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-0': true
    }">
    <!-- Page Header -->
    <d-row
      no-gutters
      :class="{
        'page-header': true,
        'mb-3': true
      }">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <h3 class="page-title">
          {{ $t('potentialCustomers.contacts') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="addNewContact">
          <i class="material-icons">add</i>
          {{ $t('potentialCustomers.addNewPotentialCustomerContact') }}
        </d-button>
      </d-col>
    </d-row>

    <d-card style="width: 100%;">
      <table
        v-if="allDataLoaded && !loadError"
        class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('potentialCustomers.description') }}</th>
            <th scope="col" class="border-0" style="width: 240px;">{{ $t('users.userName') }}</th>
            <th scope="col" class="border-0" style="width: 40px">{{ $t('customer.contact.type') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('customer.contact.contactDate') }}</th>
            <th scope="col" class="border-0" style="width: 160px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(contact, index) of contacts"
            :key="'contacts-' + index">
            <td class="align-middle">
              <small v-if="contact.description">{{ contact.description }}</small>
              <small v-else>&mdash;</small>
            </td>
            <td class="align-middle">
              {{ contact.user_name }}
            </td>
            <td class="align-middle text-center">
              <template v-for="(type, index) of contact.types">
                <i
                  v-if="type === 'EMAIL'"
                  :key="'contact-type-icon-1-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.mailContact')">email</i>
                <i
                  v-else-if="type === 'PHONE'"
                  :key="'contact-type-icon-2-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.phoneContact')">call</i>
                <i
                  v-else-if="type === 'MEETING'"
                  :key="'contact-type-icon-3-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.typeMeeting')">groups</i>
                <i
                  v-else-if="type === 'PATTERN_SENDING'"
                  :key="'contact-type-icon-4-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.typePatternSending')">texture</i>
                <i
                  v-else-if="type === 'SPEC_CAT_SENDING'"
                  :key="'contact-type-icon-5-' + index"
                  class="material-icons cursor-help"
                  :title="$t('customer.contact.typeSpecCatSending')">unarchive</i>
                <img
                  v-else-if="availableTypes.indexOf(type) > -1 && typeIcons[type]"
                  :key="'contact-type-icon-6-' + index"
                  :src="typeIcons[type]"
                  height="24"
                  width="24"
                  :alt="typeNames[type]"
                  :title="typeNames[type]">
                <i
                  v-else-if="availableTypes.indexOf(type) > -1"
                  :key="'contact-type-icon-7-' + index"
                  class="material-icons cursor-help"
                  :title="typeNames[type]">help</i>
              </template>
            </td>
            <td class="align-middle">
              {{ contact.date }}
            </td>
            <td class="align-middle">
              <d-button
                theme="primary"
                @click.prevent="showNotifications(contact.id)">
                <i class="material-icons">notifications</i>
              </d-button>

              <d-button
                theme="danger"
                :disabled="removeInProgress || !userIsAllowedToDeleteContact()"
                @click.prevent="removeContact(contact.id)"
                class="ml-2"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="contacts.length === 0">
            <td
              colspan="5"
              class="text-center">
              {{ $t('customer.contact.noContactsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded && !loadError"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <contact-popup
      v-if="showAddContactPopup"
      :potentialCustomerID="parseInt(this.$route.params.id, 10)"/>

    <contact-notifications-popup
      v-if="showContactNotificationsPopup"
      :contactID="selectedContactID"
      contactType="potential" />
  </d-container>
</template>

<script>
import ContactNotificationsPopup from '../contacts/ContactNotificationsPopup.vue';
import ContactPopup from './ContactPopup.vue';
import ListUtils from './../../utils/ListUtils.js';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'potential-customer-contacts',
  props: {
    contacts: {
      required: false,
      default: []
    }
  },
  components: {
    'contact-notifications-popup': ContactNotificationsPopup,
    'contact-popup': ContactPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedTypes;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    availableTypes () {
      return this.contactTypes.map(contactType => contactType.type);
    },
    typeIcons () {
      let icons = {};

      for (let i = 0; i < this.contactTypes.length; i++) {
        if (this.contactTypes[i].icon_url) {
          let type = this.contactTypes[i].type;
          icons[type] = this.contactTypes[i].icon_url;
        }
      }

      return icons;
    },
    typeNames () {
      let names = {};

      for (let i = 0; i < this.contactTypes.length; i++) {
        let type = this.contactTypes[i].type;
        names[type] = this.contactTypes[i].name;
      }

      return names;
    }
  },
  data() {
    return {
      selectedContactID: null,
      showAddContactPopup: false,
      showContactNotificationsPopup: false,
      removeInProgress: false,
      contactTypes: [],
      loadedTypes: false,
      loadError: false
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('close-potential-customer-new-contact-popup', this.closeNewContactPopup);
    this.$bus.$on('close-contact-notifications-popup', this.closeNotificationsPopup);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-contact-types/items',
        loadedKey: 'loadedTypes',
        outputKey: 'contactTypes',
        errorKey: 'loadError'
      });
    },
    removeContact (contactID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/potential-customers/contacts/delete/',
        id: contactID,
        successTitle: this.$t('customer.contact.removeContactSuccessTitle'),
        successText: this.$t('customer.contact.removeContactSuccessText'),
        confirmTitle: this.$t('customer.contact.removeContactConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.contact.removeContactErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('potential-customer-contacts-reload-needed')
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    editContact (contactID) {
      this.editedContactID = contactID;
      this.showEditContactPopup = true;
    },
    userIsAllowedToDeleteContact () {
      if (this.userIsAdmin) {
        return true;
      }

      return false;
    },
    addNewContact () {
      this.showAddContactPopup = true;
    },
    closeNewContactPopup () {
      this.showAddContactPopup = false;
    },
    showNotifications (contactID) {
      this.selectedContactID = contactID;
      this.showContactNotificationsPopup = true;
    },
    closeNotificationsPopup () {
      this.showContactNotificationsPopup = false;
      this.selectedContactID = null;
    }
  },
  beforeDestroy () {
    this.$bus.$off('close-potential-customer-new-contact-popup', this.closeNewContactPopup);
    this.$bus.$off('close-contact-notifications-popup', this.closeNotificationsPopup);
  }
};
</script>

<style lang="scss">
.cursor-help {
  cursor: help;
}

.truncate-multiline {
  display: -webkit-box !important;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
}
</style>
